import { Component, Inject, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { AuthStore } from '@root/app/store/auth.store';
import { AuthService } from '../../../core/services/auth.service';
import { TranslateModule } from '@ngx-translate/core';
import { AuthorizationContainerComponent } from '../authorization-container/authorization-container.component';
import { environment } from '@root/environments/environment';
import { MsalService, MSAL_GUARD_CONFIG, MsalGuardConfiguration } from '@azure/msal-angular';
import { RedirectRequest } from '@azure/msal-browser';

import { APP_PATHS, DOCS_URL } from '@core/constants';
import { isSafeInternalPath } from '../../auth-redirect.util';
import { TextFieldComponent } from '@custom-components/general/form/text-field/text-field.component';
import { FilledButtonComponent } from '@custom-components/general/button/filled-button/filled-button.component';
import { TonalButtonComponent } from '@custom-components/general/button/tonal-button/tonal-button.component';
import { AlertBannerComponent } from '@custom-components/feedback/alert-banner/alert-banner.component';
import { LoginTermsDialogComponent } from '../login-terms-dialog/login-terms-dialog.component';

@Component({
  selector: 'xemex-login-page',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    AuthorizationContainerComponent,
    TextFieldComponent,
    FilledButtonComponent,
    TonalButtonComponent,
    AlertBannerComponent,
  ],
  providers: [AuthService],
  templateUrl: './login-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './login-page.component.scss',
})
export class LoginPageComponent implements OnInit {
  APP_PATHS = APP_PATHS;
  readonly store = inject(AuthStore);
  private readonly route = inject(ActivatedRoute);
  private readonly dialog = inject(MatDialog);

  loginForm: FormGroup = new FormGroup({
    username: new FormControl('', [Validators.required, Validators.email]),
    password: new FormControl('', Validators.required),
  });
  companyName = environment.branding.text.company.name;
  privacyPolicyLink = environment.branding.urls.privacyPolicy;

  get usernameCtrl(): FormControl {
    return this.loginForm.controls['username'] as FormControl;
  }

  get passwordCtrl(): FormControl {
    return this.loginForm.controls['password'] as FormControl;
  }

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private authService: MsalService,
  ) {}

  ngOnInit(): void {
    this.store.clearAuthFeedback();

    const redirectUri = this.route.snapshot.queryParamMap.get('redirect_uri');
    if (!redirectUri) return;
    // Persist across the login (incl. the MSAL redirect round-trip). Accept the
    // allow-listed external DOCS deep-link OR an internal app path.
    try {
      if (new URL(redirectUri).origin === DOCS_URL) {
        globalThis.sessionStorage.setItem('redirect_uri', redirectUri);
      }
    } catch {
      if (isSafeInternalPath(redirectUri)) {
        globalThis.sessionStorage.setItem('redirect_uri', redirectUri);
      }
    }
  }

  /** Opens the read-only Terms of Service dialog. */
  onViewTerms(): void {
    this.dialog.open(LoginTermsDialogComponent, {
      width: '42rem',
      maxWidth: 'calc(100vw - 2rem)',
    });
  }

  onSignIn() {
    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      return;
    }
    this.store.loginMethod(this.loginForm.value);
  }

  login() {
    if (this.msalGuardConfig.authRequest) {
      this.authService.loginRedirect({
        ...this.msalGuardConfig.authRequest,
      } as RedirectRequest);
    } else {
      this.authService.loginRedirect();
    }
  }
}
